import { useUserManagement } from '../hooks/useUserManagement';
import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    Select,
    Spinner,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { Dismiss24Regular, Search24Regular } from '@fluentui/react-icons';
import { User, UserRole } from '../../../domain/users/models/User';
import { EditUserCommand } from '../../../services/providers/progpt/dtos/requests/ProGPTUserRequest';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../styles';

import _debounce from 'lodash/debounce';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        backgroundColor: customColors.protivitiOffWhite,
        ...shorthands.padding('40px', '24px', '24px', '24px'),
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        marginBottom: '24px',
        flexShrink: 0,
    },
    title: {
        margin: '0',
        textAlign: 'center',
        fontFamily: customFonts.TitleFont,
        fontSize: '48px',
        lineHeight: '1',
        color: customColors.summarizeButtonDarker,
    },
    subtitle: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        color: customColors.buttonTextDarkBlue,
        textAlign: 'center',
        maxWidth: '720px',
    },
    toolbar: {
        width: '100%',
        maxWidth: '960px',
        display: 'flex',
        justifyContent: 'center',
    },
    searchInput: {
        width: '420px',
        maxWidth: '100%',
    },
    clearButton: {
        minWidth: 'auto',
        padding: '0',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    scrollRegion: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        ...AlwaysOnScrollBarStyles,
    },
    tableCard: {
        width: '100%',
        backgroundColor: customColors.white,
        boxShadow: customColors.dropShadow1,
        ...shorthands.borderRadius('18px'),
        ...shorthands.padding('10px'),
        boxSizing: 'border-box',
    },
    table: {
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: '0',
    },
    headerRow: {
        backgroundColor: customColors.lightBlueGrayBackground2,
    },
    headerCell: {
        fontFamily: customFonts.Barlow,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.darkNavy,
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: `1px solid ${customColors.lightGray}`,
        ':first-child': {
            borderTopLeftRadius: '12px',
        },
        ':last-child': {
            borderTopRightRadius: '12px',
        },
    },
    bodyRow: {
        ':nth-child(even)': {
            backgroundColor: customColors.lightBlueGrayBackground2,
        },
    },
    cell: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        color: customColors.darkNavy,
        padding: '14px 16px',
        verticalAlign: 'middle',
        borderBottom: `1px solid ${customColors.lightGray}`,
    },
    actionCell: {
        width: '140px',
    },
    lastRowCell: {
        borderBottom: 'none',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '280px',
        ...shorthands.gap('10px'),
        color: customColors.buttonTextDarkBlue,
    },
    emptyTitle: {
        fontFamily: customFonts.Barlow,
        fontSize: '20px',
        color: customColors.darkNavy,
    },
    emptyText: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
    },
    editButton: {
        fontFamily: customFonts.Lato,
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('8px'),
        ...shorthands.padding('8px', '14px'),
        cursor: 'pointer',
        ':hover': {
            color: customColors.white,
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
    },
    pagination: {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
    },
    paginationList: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        ...shorthands.gap('8px'),
    },
    pageButton: {
        minWidth: '40px',
        height: '40px',
        fontFamily: customFonts.Lato,
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.lightGray),
        ...shorthands.borderRadius('8px'),
        ':hover': {
            color: customColors.white,
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.borderColor(customColors.protivitiOrange),
        },
    },
    pageButtonActive: {
        color: customColors.white,
        backgroundColor: customColors.protivitiDarkBlue,
        ...shorthands.borderColor(customColors.protivitiDarkBlue),
        ':hover': {
            color: customColors.white,
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.borderColor(customColors.protivitiDarkBlue),
        },
    },
    pageButtonNav: {
        minWidth: '72px',
    },
    dialogSurface: {
        maxWidth: '520px',
        backgroundColor: customColors.protivitiOffWhite,
    },
    dialogTitle: {
        fontFamily: customFonts.TitleFont,
        fontSize: '28px',
        color: customColors.summarizeButtonDarker,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        ...shorthands.gap('16px'),
        paddingTop: '8px',
    },
    dialogField: {
        width: '100%',
        minWidth: 0,
        '& label': {
            fontFamily: customFonts.Barlow,
            fontWeight: '700',
            color: customColors.darkNavy,
        },
        '& .fui-Field__control': {
            width: '100%',
            minWidth: 0,
        },
        '& .fui-Select, & select': {
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
        },
    },
    dialogInput: {
        width: '100%',
    },
    dialogInputDisabled: {
        cursor: 'not-allowed',
        '& .fui-Input, & input': {
            cursor: 'not-allowed',
        },
        '& input': {
            backgroundColor: customColors.lightBlueGrayBackground2,
            color: customColors.buttonTextDarkBlue,
            opacity: '0.75',
            filter: 'saturate(0.8)',
        },
    },
    dialogSelect: {
        display: 'block',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        fontFamily: customFonts.Lato,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.lightGray),
        ...shorthands.borderRadius('8px'),
        ...shorthands.padding('10px', '12px'),
    },
    dialogActions: {
        paddingTop: '16px',
    },
    dialogButtonSecondary: {
        fontFamily: customFonts.Lato,
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        backgroundColor: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.lightGray),
    },
    dialogButtonPrimary: {
        fontFamily: customFonts.Lato,
        fontWeight: '700',
        color: customColors.white,
        backgroundColor: customColors.protivitiOrange,
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        ':hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.borderColor(customColors.protivitiDarkBlue),
        },
    },
    loadingWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '280px',
    },
    '@media (max-width: 768px)': {
        root: {
            ...shorthands.padding('24px', '12px', '12px', '12px'),
        },
        title: {
            fontSize: '36px',
        },
        toolbar: {
            justifyContent: 'stretch',
        },
        searchInput: {
            width: '100%',
        },
        tableCard: {
            overflowX: 'auto',
        },
        table: {
            minWidth: '640px',
        },
        pageButtonNav: {
            minWidth: 'unset',
        },
    },
});

export const UserManagementPage: React.FC = () => {
    const styles = useStyles();
    const {
        users,
        selectedUser,
        totalItems,
        pageNumber,
        pageSize,
        isLoading,
        onFetchUsers,
        onUpdateUser,
        onSelectUser,
        onPageNumberChange,
        onSearchTermChange,
    } = useUserManagement();

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState<User | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const roleMapping: Record<UserRole, string> = {
        [UserRole.Admin]: 'Administrator',
        [UserRole.Creator]: 'Agent Owner',
        [UserRole.Standard]: 'User',
        [UserRole.RestrictedAccess]: 'Restricted Access',
    };

    useEffect(() => {
        void onFetchUsers();
    }, [onFetchUsers]);

    const debouncedSearchChange = useMemo(
        () =>
            _debounce((value: string) => {
                onSearchTermChange(value);
            }, 500),
        [onSearchTermChange],
    );

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel();
        };
    }, [debouncedSearchChange]);

    const numPages = Math.ceil(totalItems / pageSize);

    const handlePageChange = (page: number) => {
        onPageNumberChange(page);
    };

    const openModal = (user: User) => {
        onSelectUser(user);
        setFormData(user);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        onSelectUser(undefined);
        setFormData(null);
    };

    const handleUpdateUser = () => {
        if (selectedUser && formData) {
            const updatedFormData: EditUserCommand = {
                userId: selectedUser.id,
                name: formData.name,
                loginAddress: formData.loginAddress,
                role: formData.role,
            };
            void onUpdateUser(selectedUser.id, updatedFormData);
            setShowModal(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        debouncedSearchChange(value);
    };

    const handleClearSearch = () => {
        setSearchValue('');
    };
    const maxPagesToShow = 10;

    let startPage = Math.max(1, pageNumber - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > numPages) {
        endPage = numPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const visibleUsers = users;

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <Text as="h1" className={styles.title}>
                    User Management
                </Text>
                <Text className={styles.subtitle}>Update User Access Roles</Text>
                <div className={styles.toolbar}>
                    <Input
                        className={styles.searchInput}
                        contentBefore={<Search24Regular />}
                        contentAfter={
                            searchValue && (
                                <Button
                                    appearance="transparent"
                                    className={styles.clearButton}
                                    icon={<Dismiss24Regular />}
                                    onClick={handleClearSearch}
                                    aria-label="Clear search"
                                />
                            )
                        }
                        placeholder="Search users by name, login, or role"
                        value={searchValue}
                        onChange={(_event, data) => handleSearchChange(data.value)}
                    />
                </div>
            </div>
            <div className={styles.scrollRegion}>
                <div className={styles.tableCard}>
                    <table className={styles.table}>
                        <thead>
                            <tr className={styles.headerRow}>
                                <th className={styles.headerCell}>Name</th>
                                <th className={styles.headerCell}>Login Address</th>
                                <th className={styles.headerCell}>Role</th>
                                <th className={mergeClasses(styles.headerCell, styles.actionCell)}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className={styles.lastRowCell}>
                                        <div className={styles.loadingWrap}>
                                            <Spinner label="Loading users..." />
                                        </div>
                                    </td>
                                </tr>
                            ) : visibleUsers.length > 0 ? (
                                visibleUsers.map((user: User, index) => {
                                    const isLastRow = index === visibleUsers.length - 1;

                                    return (
                                        <tr key={user.id} className={styles.bodyRow}>
                                            <td className={mergeClasses(styles.cell, isLastRow && styles.lastRowCell)}>
                                                {user.name}
                                            </td>
                                            <td className={mergeClasses(styles.cell, isLastRow && styles.lastRowCell)}>
                                                {user.loginAddress}
                                            </td>
                                            <td className={mergeClasses(styles.cell, isLastRow && styles.lastRowCell)}>
                                                {roleMapping[user.role]}
                                            </td>
                                            <td
                                                className={mergeClasses(
                                                    styles.cell,
                                                    styles.actionCell,
                                                    isLastRow && styles.lastRowCell,
                                                )}
                                            >
                                                <Button className={styles.editButton} onClick={() => openModal(user)}>
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={4} className={styles.lastRowCell}>
                                        <div className={styles.emptyState}>
                                            <Text className={styles.emptyTitle}>No users found</Text>
                                            <Text className={styles.emptyText}>
                                                Try adjusting the search term or refresh the current page.
                                            </Text>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.pagination}>
                <div className={styles.paginationList}>
                    <Button
                        className={mergeClasses(styles.pageButton, styles.pageButtonNav)}
                        disabled={pageNumber === 1}
                        onClick={() => handlePageChange(1)}
                    >
                        First
                    </Button>
                    <Button
                        className={mergeClasses(styles.pageButton, styles.pageButtonNav)}
                        disabled={pageNumber === 1}
                        onClick={() => handlePageChange(pageNumber - 1)}
                    >
                        Prev
                    </Button>
                    {[...Array(endPage - startPage + 1).keys()].map((i) => {
                        const page = startPage + i;
                        return (
                            <Button
                                key={page}
                                className={mergeClasses(
                                    styles.pageButton,
                                    page === pageNumber && styles.pageButtonActive,
                                )}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </Button>
                        );
                    })}
                    <Button
                        className={mergeClasses(styles.pageButton, styles.pageButtonNav)}
                        onClick={() => handlePageChange(pageNumber + 1)}
                        disabled={pageNumber === numPages}
                    >
                        Next
                    </Button>
                    <Button
                        className={mergeClasses(styles.pageButton, styles.pageButtonNav)}
                        onClick={() => handlePageChange(numPages)}
                        disabled={pageNumber === numPages}
                    >
                        Last
                    </Button>
                </div>
            </div>

            <Dialog open={showModal} onOpenChange={(_event, data) => !data.open && closeModal()}>
                <DialogSurface className={styles.dialogSurface}>
                    <DialogBody>
                        <DialogTitle className={styles.dialogTitle}>Edit User</DialogTitle>
                        <DialogContent className={styles.dialogContent}>
                            <Field className={styles.dialogField} label="Name">
                                <Input
                                    className={mergeClasses(styles.dialogInput, styles.dialogInputDisabled)}
                                    value={formData?.name ?? ''}
                                    disabled
                                />
                            </Field>
                            <Field className={styles.dialogField} label="Login Address">
                                <Input
                                    className={mergeClasses(styles.dialogInput, styles.dialogInputDisabled)}
                                    value={formData?.loginAddress ?? ''}
                                    disabled
                                />
                            </Field>
                            <Field className={styles.dialogField} label="Role">
                                <Select
                                    value={formData?.role?.toString() ?? '2'}
                                    onChange={(event) => {
                                        const role = Number(event.target.value) as UserRole;
                                        setFormData((current) =>
                                            current
                                                ? {
                                                      ...current,
                                                      role,
                                                  }
                                                : current,
                                        );
                                    }}
                                >
                                    {Object.entries(roleMapping).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                        </DialogContent>
                        <DialogActions className={styles.dialogActions}>
                            <Button
                                className={styles.dialogButtonSecondary}
                                appearance="secondary"
                                onClick={closeModal}
                            >
                                Cancel
                            </Button>
                            <Button className={styles.dialogButtonPrimary} onClick={handleUpdateUser}>
                                Update User
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default UserManagementPage;
